import { z } from 'zod';

const CustomFieldsRecord = z.record(z.string(), z.unknown());

export const BILLING_MODELS = ['retainer', 'tm', 'metrics'] as const;
export type BillingModel = (typeof BILLING_MODELS)[number];

export const CADENCES = ['monthly', 'quarterly', 'semi_annual', 'annual', 'one_time'] as const;
export type Cadence = (typeof CADENCES)[number];

export const SOW_TYPES = ['retainer', 'tm', 'metrics', 'hybrid'] as const;
export type SowType = (typeof SOW_TYPES)[number];

// ---- Engagement JSONB shapes (loose on client — server validates)

export const EngagementActivitySchema = z.object({ productId: z.string().uuid() });
export type EngagementActivity = z.infer<typeof EngagementActivitySchema>;

export const EngagementDiscountSchema = z.object({
  kind: z.enum(['percentage', 'fixed_amount']),
  amount: z.number(),
});
export type EngagementDiscount = z.infer<typeof EngagementDiscountSchema>;

export const EngagementRateSchema = z.object({
  roleId: z.string().uuid(),
  // Effective billing rate. `baseRate` is the rate-card rate snapshotted when
  // the role was added; rate ≠ baseRate marks a customer-customized rate.
  rate: z.number(),
  baseRate: z.number(),
});
export type EngagementRate = z.infer<typeof EngagementRateSchema>;

/** A flat price for a band of the metric's QUANTITY — not a date band.
 *  Half-open `[fromQty, toQty)`; `toQty: null` is the unbounded top band. */
export const EngagementTierSchema = z.object({
  fromQty: z.number(),
  toQty: z.number().nullable(),
  price: z.number(),
});
export type EngagementTier = z.infer<typeof EngagementTierSchema>;

// ---- Row types -------------------------------------------------------------

export const SowSchema = z.object({
  id: z.string().uuid(),
  version: z.number().int().positive(),
  tenantId: z.string().uuid(),
  customerId: z.string().uuid(),
  name: z.string(),
  documentNo: z.string().nullable(),
  rateCardId: z.string().uuid().nullable(),
  rateCardVersion: z.number().int().nullable(),
  signedAt: z.string().nullable(),
  signedBy: z.string().nullable(),
  effectiveFrom: z.string(),
  effectiveTo: z.string().nullable(),
  /** Invoicing pause for this SOW, as a half-open window [from, until) —
   *  same shape as the customer-level pause. `from` null = not paused. */
  invoicingPauseFrom: z.string().nullable(),
  invoicingPauseUntil: z.string().nullable(),
  customFields: CustomFieldsRecord,
  createdAt: z.string(),
  // Derived on detail endpoints; absent on list endpoints.
  sowType: z.enum(SOW_TYPES).nullable().optional(),
});
export type Sow = z.infer<typeof SowSchema>;

/**
 * An engagement as stored: what is billed under a SOW.
 *
 * `id` is STABLE across amendments — budgets, timesheet stamps and invoice
 * lines point at it — while the row itself rides `sowVersion`. Model-specific
 * fields are nullable on the wire because one table carries all three models;
 * the server's shape CHECK is what guarantees the right set is populated.
 */
export const EngagementSchema = z.object({
  id: z.string().uuid(),
  sowId: z.string().uuid(),
  sowVersion: z.number().int(),
  tenantId: z.string().uuid(),
  customerId: z.string().uuid(),
  name: z.string(),
  billingModel: z.enum(BILLING_MODELS),
  description: z.string().nullable(),
  invoiceStart: z.string().nullable(),
  invoiceEnd: z.string().nullable(),
  /** NULL means INHERIT, not unset: cadence from the customer, terms from the
   *  consolidation from the customer. */
  billingCadence: z.enum(CADENCES).nullable(),
  consolidate: z.boolean().nullable(),
  /** Every invoice built from this engagement is flagged "Require manual
   *  review". Never inherited: false means no. */
  manual: z.boolean(),
  /** EMPTY MATCHES EVERYTHING — an engagement that names no activities is a
   *  wildcard, not one with missing data. */
  activities: z.array(EngagementActivitySchema),
  /** numeric(12,2) — a string on the wire, never a JS number. */
  fee: z.string().nullable(),
  discount: EngagementDiscountSchema.nullable(),
  overageRates: z.array(EngagementRateSchema).nullable(),
  rates: z.array(EngagementRateSchema).nullable(),
  metricName: z.string().nullable(),
  tiers: z.array(EngagementTierSchema).nullable(),
  createdAt: z.string(),
});
export type Engagement = z.infer<typeof EngagementSchema>;

/** What `/api/sows/all-versions` carries of each version's engagements: enough
 *  to name its billing models and what it bills, without the rate and tier
 *  arrays. */
export type EngagementSummary = Pick<
  Engagement,
  'id' | 'name' | 'billingModel' | 'fee' | 'discount' | 'billingCadence'
>;

/** A SOW version as `/api/sows/all-versions` lists it. */
export type SowWithEngagementSummary = Sow & { engagements: EngagementSummary[] };

// ---- Inputs ----------------------------------------------------------------

/**
 * Fields every engagement input carries.
 *
 * `id` is optional on the way in and STABLE on the way out: an amendment sends
 * back the ids it was given so each engagement is carried forward rather than
 * replaced, and omitting it means "this one is new". Dropping an id orphans
 * every budget and timesheet stamp pointing at that engagement.
 */
const EngagementCommonInput = {
  id: z.string().uuid().optional(),
  name: z.string().min(1),
  description: z.string().nullable().optional(),
  invoiceStart: z.string().nullable().optional(),
  invoiceEnd: z.string().nullable().optional(),
  billingCadence: z.enum(CADENCES).nullable().optional(),
  consolidate: z.boolean().nullable().optional(),
  /** Absent is false. */
  manual: z.boolean().optional(),
  activities: z.array(EngagementActivitySchema),
};

const RetainerEngagementInputSchema = z.object({
  ...EngagementCommonInput,
  billingModel: z.literal('retainer'),
  fee: z.number().nonnegative(),
  discount: EngagementDiscountSchema.nullable().optional(),
  overageRates: z.array(EngagementRateSchema).nullable().optional(),
});
export type RetainerEngagementInput = z.infer<typeof RetainerEngagementInputSchema>;

const TmEngagementInputSchema = z.object({
  ...EngagementCommonInput,
  billingModel: z.literal('tm'),
  rates: z.array(EngagementRateSchema).min(1),
});
export type TmEngagementInput = z.infer<typeof TmEngagementInputSchema>;

const MetricsEngagementInputSchema = z.object({
  ...EngagementCommonInput,
  billingModel: z.literal('metrics'),
  metricName: z.string().min(1),
  tiers: z.array(EngagementTierSchema).min(1),
});
export type MetricsEngagementInput = z.infer<typeof MetricsEngagementInputSchema>;

export const EngagementInput = z.discriminatedUnion('billingModel', [
  RetainerEngagementInputSchema,
  TmEngagementInputSchema,
  MetricsEngagementInputSchema,
]);
export type EngagementInput = z.infer<typeof EngagementInput>;

const SowCommonInput = z.object({
  customerId: z.string().uuid(),
  name: z.string().min(1),
  documentNo: z.string().nullable().optional(),
  rateCardId: z.string().uuid().nullable().optional(),
  rateCardVersion: z.number().int().positive().nullable().optional(),
  signedAt: z.string().nullable().optional(),
  signedBy: z.string().nullable().optional(),
  effectiveFrom: z.string(),
  effectiveTo: z.string().nullable().optional(),
  invoicingPauseFrom: z.string().nullable().optional(),
  invoicingPauseUntil: z.string().nullable().optional(),
  engagements: z.array(EngagementInput),
  customFields: CustomFieldsRecord.optional(),
});

export const CreateSowInput = SowCommonInput;
export type CreateSowInput = z.infer<typeof CreateSowInput>;

export const ReplaceSowInput = SowCommonInput.extend({
  expectedVersion: z.number().int().positive(),
});
export type ReplaceSowInput = z.infer<typeof ReplaceSowInput>;

export const PatchSowInput = z.object({
  expectedVersion: z.number().int().positive(),
  name: z.string().min(1).optional(),
  documentNo: z.string().nullable().optional(),
  signedAt: z.string().nullable().optional(),
  signedBy: z.string().nullable().optional(),
  invoicingPauseFrom: z.string().nullable().optional(),
  invoicingPauseUntil: z.string().nullable().optional(),
  customFields: CustomFieldsRecord.optional(),
});
export type PatchSowInput = z.infer<typeof PatchSowInput>;

export interface ListSowsQuery {
  q?: string;
  customerId?: string;
  asOf?: string;
  /** Optional single custom-field filter — see customers/types.ts. */
  customFieldId?: string;
  customFieldValue?: string;
  sortField?: 'name' | 'documentNo' | 'effectiveFrom' | 'createdAt';
  sortDirection?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

// ---- Import / export -------------------------------------------------------

/** One SOW's outcome in an import report. `sheet` is which TAB `rowNumber`
 *  counts on — in a four-sheet workbook a row number alone points at four
 *  different places. */
export interface ImportSowRow {
  rowNumber: number;
  sheet: string;
  outcome: 'created' | 'updated' | 'unchanged' | 'failed';
  qbId: string | null;
  sowName: string | null;
  errors?: string[];
}

export interface ImportSowResult {
  totalRows: number;
  created: number;
  updated: number;
  /** SOWs the file asserted exactly as they already stand. A re-uploaded
   *  export is all of these, which is the point: amending them would mint a
   *  version and re-stamp every timesheet underneath for no change. */
  unchanged: number;
  failed: number;
  rows: ImportSowRow[];
}
